import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './app/Layout/Layout';
import { ProtectedRoute, AdminRoute, SuperAdminRoute, ScrollToTop } from './components';
import AnunciosConsolTech from './components/AnunciosConsolTech';
import { ROUTES } from './utils';
import { useAuthStore, useShopStore } from './store';

// Lazy-loaded pages
const Login = lazy(() => import('./pages/Login/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Customers = lazy(() => import('./pages/Customers/Customers'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail/CustomerDetail'));
const Vehicles = lazy(() => import('./pages/Vehicles/Vehicles'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail/VehicleDetail'));
const WorkOrders = lazy(() => import('./pages/WorkOrders/WorkOrders'));
const WorkOrderDetail = lazy(() => import('./pages/WorkOrderDetail/WorkOrderDetail'));
const Services = lazy(() => import('./pages/Services/Services'));
const Users = lazy(() => import('./pages/Users/Users'));
const Portal = lazy(() => import('./pages/Portal/Portal'));
const Calendar = lazy(() => import('./pages/Calendar/Calendar'));
const Appointment = lazy(() => import('./pages/Appointment/Appointment'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Quotes = lazy(() => import('./pages/Quotes/Quotes'));
const TechnicianWorkload = lazy(() => import('./pages/TechnicianWorkload/TechnicianWorkload'));
const PartsReport = lazy(() => import('./pages/PartsReport/PartsReport'));
const Inventory = lazy(() => import('./pages/Inventory/Inventory'));
const Reminders = lazy(() => import('./pages/Reminders/Reminders'));
const ShopSelector = lazy(() => import('./pages/ShopSelector/ShopSelector'));
const Shops = lazy(() => import('./pages/Shops/Shops'));
const Inspections = lazy(() => import('./pages/Inspections/Inspections'));
const Ratings = lazy(() => import('./pages/Ratings/Ratings'));
const Survey = lazy(() => import('./pages/Survey/Survey'));
const FollowUps = lazy(() => import('./pages/FollowUps/FollowUps'));
const Loyalty = lazy(() => import('./pages/Loyalty/Loyalty'));
const FollowUp = lazy(() => import('./pages/FollowUp/FollowUp'));
const LabelPrint = lazy(() => import('./pages/LabelPrint/LabelPrint'));
const Shipments = lazy(() => import('./pages/Shipments/Shipments'));
const Landing = lazy(() => import('./pages/Landing/Landing'));
const Adquirir = lazy(() => import('./pages/Adquirir/Adquirir'));
const Leads = lazy(() => import('./pages/Leads/Leads'));
const OnlineUsers = lazy(() => import('./pages/OnlineUsers/OnlineUsers'));

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: '0.75rem',
      color: 'var(--text-secondary)',
      fontSize: 'var(--font-size-sm)',
    }}>
      <div style={{
        width: 28,
        height: 28,
        border: '3px solid var(--border-color)',
        borderTopColor: 'var(--color-primary)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      Cargando...
    </div>
  );
}

function App() {
  const { isAuthenticated, user } = useAuthStore();
  const { selectedShop } = useShopStore();

  // Determine the default route based on user role
  const getDefaultRoute = () => {
    if (!isAuthenticated) return ROUTES.LOGIN;
    if (user?.role === 'superadmin') {
      return selectedShop ? ROUTES.DASHBOARD : ROUTES.SELECT_SHOP;
    }
    return ['admin', 'superadmin'].includes(user?.role) ? ROUTES.DASHBOARD : '/workorders';
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      {/* No pinta nada: identifica al usuario ante el widget de anuncios. */}
      <AnunciosConsolTech />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Rutas públicas - fuera del Layout */}
          <Route path="/" element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <Landing />} />
          <Route path="/inicio" element={<Landing />} />
          <Route path="/adquirir" element={<Adquirir />} />
          <Route path="/portal/:slug" element={<Portal />} />
          <Route path="/agendar/:slug" element={<Appointment />} />
          <Route path="/valorar/:slug/:token" element={<Survey />} />
          <Route path="/seguimiento/:slug/:token" element={<FollowUp />} />
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <Login />
            }
          />

          <Route element={<Layout />}>

            {/* Forgot Password - solo si no está autenticado */}
            <Route
              path="forgot-password"
              element={
                isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <ForgotPassword />
              }
            />

            {/* Reset Password - solo si no está autenticado */}
            <Route
              path="reset-password/:token"
              element={
                isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <ResetPassword />
              }
            />

            {/* Admin only routes */}
            <Route
              path="dashboard"
              element={
                <AdminRoute>
                  <Dashboard />
                </AdminRoute>
              }
            />
            <Route
              path="customers"
              element={
                <AdminRoute>
                  <Customers />
                </AdminRoute>
              }
            />
            <Route
              path="customers/:id"
              element={
                <AdminRoute>
                  <CustomerDetail />
                </AdminRoute>
              }
            />
            <Route
              path="vehicles"
              element={
                <AdminRoute>
                  <Vehicles />
                </AdminRoute>
              }
            />
            <Route
              path="vehicles/:id"
              element={
                <AdminRoute>
                  <VehicleDetail />
                </AdminRoute>
              }
            />
            <Route
              path="services"
              element={
                <AdminRoute>
                  <Services />
                </AdminRoute>
              }
            />
            <Route
              path="calendar"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="quotes"
              element={
                <AdminRoute>
                  <Quotes />
                </AdminRoute>
              }
            />
            <Route
              path="parts-report"
              element={
                <AdminRoute>
                  <PartsReport />
                </AdminRoute>
              }
            />
            <Route
              path="inventory"
              element={
                <AdminRoute>
                  <Inventory />
                </AdminRoute>
              }
            />
            <Route
              path="reminders"
              element={
                <AdminRoute>
                  <Reminders />
                </AdminRoute>
              }
            />
            <Route
              path="ratings"
              element={
                <AdminRoute>
                  <Ratings />
                </AdminRoute>
              }
            />
            <Route
              path="follow-ups"
              element={
                <AdminRoute>
                  <FollowUps />
                </AdminRoute>
              }
            />
            <Route
              path="loyalty"
              element={
                <AdminRoute>
                  <Loyalty />
                </AdminRoute>
              }
            />
            <Route
              path="inventory/labels"
              element={
                <AdminRoute>
                  <LabelPrint />
                </AdminRoute>
              }
            />
            <Route
              path="shipments"
              element={
                <AdminRoute>
                  <Shipments />
                </AdminRoute>
              }
            />

            {/* Routes for all authenticated users (admin and technicians) */}
            <Route
              path="workorders"
              element={
                <ProtectedRoute>
                  <WorkOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="workorders/:id"
              element={
                <ProtectedRoute>
                  <WorkOrderDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="inspections"
              element={
                <AdminRoute>
                  <Inspections />
                </AdminRoute>
              }
            />
            <Route
              path="workload"
              element={
                <ProtectedRoute>
                  <TechnicianWorkload />
                </ProtectedRoute>
              }
            />

            {/* Superadmin routes */}
            <Route
              path="select-shop"
              element={
                <SuperAdminRoute>
                  <ShopSelector />
                </SuperAdminRoute>
              }
            />
            <Route
              path="shops"
              element={
                <SuperAdminRoute>
                  <Shops />
                </SuperAdminRoute>
              }
            />
            <Route
              path="leads"
              element={
                <SuperAdminRoute>
                  <Leads />
                </SuperAdminRoute>
              }
            />
            <Route
              path="online-users"
              element={
                <SuperAdminRoute>
                  <OnlineUsers />
                </SuperAdminRoute>
              }
            />

            {/* Admin only routes */}
            <Route
              path="users"
              element={
                <AdminRoute>
                  <Users />
                </AdminRoute>
              }
            />
            <Route
              path="settings"
              element={
                <AdminRoute>
                  <Settings />
                </AdminRoute>
              }
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
